import { format, isValid, parseISO } from 'date-fns';
import {
  isPriceListRerunRunning,
  PriceListRerunAvailability,
  PriceListRerunExecution,
  PriceListRerunResponse,
  priceListRerunCandidates,
} from '../../../../../services/PriceListRerunService';
import { RerunCardContent, RerunCount } from './types';

export const RUNNING_POLL_MS = 5000;
export const WAITING_ENGINE_POLL_MS = 60000;

export const formatRerunTime = (value?: string | null) => {
  if (!value) return null;
  const parsed = parseISO(value);
  return isValid(parsed) ? format(parsed, 'HH:mm') : null;
};

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

export const describeRerunRefusal = (r: PriceListRerunResponse) => {
  if (r.httpStatus === 403) return 'O reprocessamento da tabela de preços não está liberado para a sua conta.';
  if (r.httpStatus === 409 && (r.message ?? '').includes('já existe')) {
    return 'A tabela de preços já foi reprocessada hoje. Um novo reprocessamento fica disponível amanhã, depois do motor.';
  }
  if (r.httpStatus === 409) {
    return 'O motor de precificação ainda não terminou a rodada de hoje. Assim que ele terminar, o reprocessamento fica disponível.';
  }
  return 'Não foi possível falar com o reprocessamento agora. Tente novamente em alguns minutos.';
};

export const executionCounts = (execution: PriceListRerunExecution): RerunCount[] => [
  { value: execution.written ?? 0, label: 'produtos atualizados', tone: 'success' },
  { value: execution.skippedApplied ?? 0, label: 'mantidos (aplicados ou editados hoje)' },
  { value: execution.skippedOther ?? 0, label: 'não reprocessados' },
];

export const previewCounts = (preview: PriceListRerunResponse): RerunCount[] => [
  { value: priceListRerunCandidates(preview), label: 'itens novos ou alterados serão reprocessados', tone: 'info' },
  { value: preview.skippedApplied ?? 0, label: 'aplicados ou editados hoje ficam como estão' },
  { value: preview.skippedOther ?? 0, label: 'não podem ser reprocessados' },
];

/** Consulta de novo só enquanto algo pode mudar sozinho: execução rodando ou motor ainda sem terminar. */
export const rerunRefetchInterval = (data?: PriceListRerunAvailability) => {
  if (isPriceListRerunRunning(data?.execution?.status)) return RUNNING_POLL_MS;
  if (data?.enabled && !data.engineFinished && !data.execution) return WAITING_ENGINE_POLL_MS;
  return false;
};

const runningContent = (execution: PriceListRerunExecution): RerunCardContent => ({
  tone: 'info',
  badge: 'Em andamento',
  text:
    `Reprocessando ${plural(priceListRerunCandidates(execution), 'item novo', 'itens novos')} da tabela. ` +
    'Você pode sair desta tela: avisamos quando terminar.',
});

const finishedContent = (execution: PriceListRerunExecution): RerunCardContent => {
  const time = formatRerunTime(execution.finishedAt);
  if (execution.status === 'CONCLUIDO_COM_FALHAS') {
    return {
      tone: 'warning',
      badge: 'Concluído com falhas',
      text: 'Alguns itens não foram atualizados. Se precisar, fale com o suporte.',
      counts: executionCounts(execution),
    };
  }
  return {
    tone: 'success',
    badge: `Concluído${time ? ` às ${time}` : ''}`,
    text: 'Os preços da tabela já estão no Gerenciador. Um novo reprocessamento fica disponível amanhã, depois do motor.',
    counts: executionCounts(execution),
  };
};

const WAITING_ENGINE_CONTENT: RerunCardContent = {
  tone: 'neutral',
  badge: 'Aguardando o motor',
  text: 'Assim que o motor de precificação terminar a rodada de hoje, você pode reprocessar os itens novos da tabela sem esperar o dia seguinte.',
};

const availableContent = (engineFinishedAt?: string | null): RerunCardContent => {
  const time = formatRerunTime(engineFinishedAt);
  return {
    tone: 'success',
    badge: 'Disponível',
    text:
      `O motor terminou${time ? ` às ${time}` : ' hoje'}. Enviou uma tabela nova depois disso? ` +
      'Reprocesse agora só os itens novos ou alterados; o restante do Gerenciador não muda.',
  };
};

export const buildRerunCardContent = (availability: PriceListRerunAvailability): RerunCardContent => {
  const { execution } = availability;
  if (execution && isPriceListRerunRunning(execution.status)) return runningContent(execution);
  if (execution) return finishedContent(execution);
  if (!availability.engineFinished) return WAITING_ENGINE_CONTENT;
  return availableContent(availability.engineFinishedAt);
};
